import React, { useMemo } from 'react';

import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import { useGetInclusionsCategories } from '~/hooks/apis/inclusions/useGetInclusionsCategories';

import InclusionRow from './InclusionRow';
import { type MasterInclusion, TABLE_GRID_COLUMNS, createMasterInclusion, getPackageOptions } from './utils';

interface Props {
  inclusions: Array<MasterInclusion>;
  isLoadingInclusions?: boolean;
  onChange: React.Dispatch<React.SetStateAction<Array<MasterInclusion>>>;
  packages?: Array<App.Package>;
}

const FALLBACK_CATEGORY_OPTION = 'Other';

export default function Inclusions({ inclusions, isLoadingInclusions, onChange, packages }: Props) {
  const { data: inclusionCategories = [] } = useGetInclusionsCategories();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const packageOptions = useMemo(() => getPackageOptions(packages), [packages]);
  const inclusionIds = useMemo(() => inclusions.map((inclusion) => inclusion.id), [inclusions]);
  const categoryOptions = useMemo(() => {
    const fetchedCategories = inclusionCategories.map(({ category }) => category);
    const offerCategories = inclusions.map((inclusion) => inclusion.category).filter((category) => !!category);

    return Array.from(new Set(fetchedCategories.concat(offerCategories, FALLBACK_CATEGORY_OPTION)));
  }, [inclusionCategories, inclusions]);

  const updateInclusion = (id: string, nextValues: Partial<MasterInclusion>) => {
    onChange((currentInclusions) =>
      currentInclusions.map((inclusion) => (inclusion.id === id ? { ...inclusion, ...nextValues } : inclusion)),
    );
  };

  const deleteInclusion = (id: string) => {
    onChange((currentInclusions) => currentInclusions.filter((inclusion) => inclusion.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    onChange((currentInclusions) => {
      const oldIndex = currentInclusions.findIndex((inclusion) => inclusion.id === active.id);
      const newIndex = currentInclusions.findIndex((inclusion) => inclusion.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return currentInclusions;
      }

      return arrayMove(currentInclusions, oldIndex, newIndex);
    });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Inclusions</Typography>
          <Typography variant="body1" color="text.secondary">
            Master list of inclusions across all offer packages. Sets the default ordering; each package's own list is
            kept for manual overrides.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            onChange((currentInclusions) => currentInclusions.concat(createMasterInclusion(packageOptions)))
          }
          sx={{ flexShrink: 0 }}
        >
          Add inclusion
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Drag <DragIndicatorIcon fontSize="inherit" sx={{ verticalAlign: 'text-bottom' }} /> to reorder. Inclusions
        follow this master order. Any inclusion assigned to only one package is shown first within that package.
      </Typography>

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              bgcolor: 'grey.50',
              display: { xs: 'none', lg: 'grid' },
              gap: 2,
              gridTemplateColumns: TABLE_GRID_COLUMNS,
              px: 2,
              py: 1.5,
            }}
          >
            <Box />
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Category
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Inclusion
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              Highlight
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              LuxPlus only
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Assigned packages
            </Typography>
            <Box />
          </Box>

          {isLoadingInclusions && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Loading inclusions...
              </Typography>
            </Box>
          )}

          {!isLoadingInclusions && inclusions.length === 0 && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No inclusions are currently available for this offer.
              </Typography>
            </Box>
          )}

          {!isLoadingInclusions && inclusions.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={inclusionIds} strategy={verticalListSortingStrategy}>
                {inclusions.map((inclusion) => (
                  <InclusionRow
                    key={inclusion.id}
                    categoryOptions={categoryOptions}
                    inclusion={inclusion}
                    packageOptions={packageOptions}
                    onChange={updateInclusion}
                    onDelete={deleteInclusion}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
